import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { PrintFlowMark } from "../../components/layout/PrintFlowMark";
import { Aviso, Boton, Campo } from "../../components/ui/primitivos";
import { API_BASE } from "../../lib/api";

/**
 * Aceptar una invitación (ADR 0035).
 *
 * Vive **fuera** de `AppLayout` y del árbol autenticado: quien llega acá todavía
 * no tiene cuenta, así que no hay sidebar, ni permisos, ni sesión que consultar.
 *
 * El enlace del mail es `/aceptar-invitacion?id=…` y esa forma no se cambia: ya
 * salió en invitaciones que pueden estar sin aceptar en alguna casilla.
 *
 * No usa `apiGet`/`apiMutar` porque esos mandan `credentials: "include"` y
 * asumen sesión; acá el request es anónimo y la respuesta del POST es la que
 * **trae** la cookie.
 */

const MINIMO_CONTRASENA = 8;

interface Invitacion {
  email: string;
  rol: string | null;
}

export function AceptarInvitacionPagina() {
  const [params] = useSearchParams();
  const id = params.get("id") ?? "";
  const navegar = useNavigate();

  const [invitacion, setInvitacion] = useState<Invitacion | null>(null);
  const [verificando, setVerificando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [nombre, setNombre] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [repetida, setRepetida] = useState("");
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    if (!id) {
      setError("El enlace no trae la invitación. Revisá que lo hayas copiado entero.");
      setVerificando(false);
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/v1/aceptar-invitacion/${encodeURIComponent(id)}`);
        if (!r.ok) throw new Error("no sirve");
        setInvitacion((await r.json()) as Invitacion);
      } catch {
        setError(
          "Esta invitación no es válida, ya se usó o venció. Pedile a quien te invitó que te mande una nueva.",
        );
      } finally {
        setVerificando(false);
      }
    })();
  }, [id]);

  const noCoinciden = repetida.length > 0 && contrasena !== repetida;
  const completo =
    nombre.trim().length > 0 && contrasena.length >= MINIMO_CONTRASENA && contrasena === repetida;

  async function aceptar() {
    setTrabajando(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/v1/aceptar-invitacion/${encodeURIComponent(id)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "printflow" },
        // La cookie de sesión viene EN esta respuesta: sin `include` el
        // navegador la descarta y habría que volver a loguearse enseguida.
        credentials: "include",
        body: JSON.stringify({ nombre: nombre.trim(), contrasena }),
      });
      if (!r.ok) {
        const cuerpo = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(cuerpo.error ?? "No se pudo crear la cuenta");
      }
      // Recarga entera y no `navigate`: el `SessionProvider` resuelve la sesión
      // una vez al montar, así que sin recargar la app seguiría creyendo que no
      // hay nadie logueado.
      window.location.assign("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo crear la cuenta");
      setTrabajando(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 p-4">
      <div className="w-full max-w-md space-y-6 rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <PrintFlowMark className="h-10 w-10" />
          <div>
            <h1 className="font-semibold text-neutral-900 text-xl">PrintFlow</h1>
            <p className="text-neutral-500 text-sm">Te invitaron a usar el sistema.</p>
          </div>
        </div>

        {verificando ? (
          <p className="text-neutral-500 text-sm">Verificando la invitación...</p>
        ) : null}

        {error ? <Aviso>{error}</Aviso> : null}

        {invitacion ? (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void aceptar();
            }}
          >
            {/* El email no se edita: la invitación es PARA esa dirección. */}
            <Campo etiqueta="Email" value={invitacion.email} readOnly disabled />
            {invitacion.rol ? (
              <p className="text-neutral-500 text-sm">
                Vas a entrar con el rol <strong>{invitacion.rol}</strong>.
              </p>
            ) : null}

            <Campo
              etiqueta="Tu nombre"
              autoComplete="name"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
            <Campo
              etiqueta="Contraseña"
              type="password"
              autoComplete="new-password"
              ayuda={`Al menos ${MINIMO_CONTRASENA} caracteres.`}
              value={contrasena}
              onChange={(e) => setContrasena(e.target.value)}
            />
            <Campo
              etiqueta="Repetir la contraseña"
              type="password"
              autoComplete="new-password"
              value={repetida}
              onChange={(e) => setRepetida(e.target.value)}
            />

            {noCoinciden ? <Aviso>Las dos contraseñas no coinciden.</Aviso> : null}

            <Boton type="submit" className="w-full" disabled={trabajando || !completo}>
              {trabajando ? "Creando la cuenta..." : "Crear mi cuenta"}
            </Boton>
          </form>
        ) : null}

        {!verificando && !invitacion ? (
          <Boton variante="secundario" className="w-full" onClick={() => navegar("/")}>
            Ir al inicio
          </Boton>
        ) : null}
      </div>
    </div>
  );
}
